"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CalloutOptions = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const slate_react_1 = require("slate-react");
const slate_1 = require("slate");
const ArgsPane_1 = require("@jsonjoy.com/ui/lib/4-card/ContextMenu/ArgsPane");
const context_1 = require("@jsonjoy.com/ui/lib/4-card/Popup/context");
const context_2 = require("@jsonjoy.com/ui/lib/styles/context");
const useSyncStore_1 = require("@jsonjoy.com/ui/lib/hooks/useSyncStore");
const colors_1 = require("./colors");
const settings_1 = require("./settings");
const letter_case_1 = tslib_1.__importDefault(require("iconista/lib/react/tabler/letter-case"));
const CharsIcon = (props) => (React.createElement(letter_case_1.default, { width: 16, height: 16, ...props }));
const renderCharsIcon = () => React.createElement(CharsIcon, null);
const variantOptionIcon = (v) => {
    const Icon = settings_1.VARIANT_ICONS[v];
    return () => React.createElement(Icon, null);
};
const isDefaultable = (v) => !!v && typeof v === 'object' && 'def' in v;
/**
 * Args pane for a callout. Real-time updates — no Apply/Cancel.
 */
const CalloutOptions = ({ element, closePopup }) => {
    const editor = (0, slate_react_1.useSlateStatic)();
    const styles = (0, context_2.useStyles)();
    const popup = (0, context_1.usePopup)();
    const onCancel = React.useCallback(() => {
        if (closePopup)
            closePopup();
        else
            popup?.close();
    }, [closePopup, popup]);
    const elementRef = React.useRef(element);
    elementRef.current = element;
    const item = React.useMemo(() => ({ name: 'Callout options', compact: true }), []);
    const setField = React.useCallback((field, value) => {
        const e = elementRef.current;
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, e);
            if (value === undefined || value === '') {
                slate_1.Transforms.unsetNodes(editor, field, { at: path });
            }
            else {
                slate_1.Transforms.setNodes(editor, { [field]: value }, { at: path });
            }
        }
        catch { }
    }, [editor]);
    const setStringField = React.useCallback((field, value) => {
        const e = elementRef.current;
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, e);
            if (value === undefined) {
                slate_1.Transforms.unsetNodes(editor, field, { at: path });
            }
            else {
                slate_1.Transforms.setNodes(editor, { [field]: value }, { at: path });
            }
        }
        catch { }
    }, [editor]);
    const params = React.useMemo(() => {
        const e = element;
        const variant = (0, settings_1.getCalloutVariant)(e.variant);
        const variantAccent = (0, settings_1.getVariantAccent)(styles, variant);
        const variantTitle = settings_1.VARIANT_TITLE[variant];
        const accentHex = (0, colors_1.toHex)(variantAccent);
        const list = [];
        list.push({
            kind: 'select',
            id: 'variant',
            name: 'Type',
            default: variant,
            options: settings_1.CALLOUT_VARIANTS.map((v) => ({
                id: v,
                name: settings_1.CALLOUT_VARIANT_LABEL[v],
                icon: variantOptionIcon(v),
            })),
        });
        list.push({
            kind: 'color',
            id: 'color',
            name: 'Accent',
            defaultable: true,
            default: accentHex,
            alpha: false,
            initialDef: e.color === undefined,
            initialValue: e.color ? (0, colors_1.toHex)(e.color) : accentHex,
        });
        list.push({ name: 'sep', innerSep: true });
        list.push({
            kind: 'bool',
            id: 'hideHeader',
            name: 'Hide header',
            default: !!e.hideHeader,
        });
        const headerVisible = !e.hideHeader;
        list.push({
            kind: 'char',
            id: 'icon',
            name: 'Icon',
            icon: renderCharsIcon,
            length: 2,
            emoji: true,
            placeholder: '',
            defaultable: true,
            default: '',
            initialDef: e.icon === undefined,
            initialValue: e.icon ?? '',
            visible: (0, useSyncStore_1.syncStore)(headerVisible),
        });
        list.push({
            kind: 'str',
            id: 'title',
            name: 'Title',
            placeholder: variantTitle,
            defaultable: true,
            default: variantTitle,
            initialDef: e.title === undefined,
            initialValue: e.title ?? '',
            visible: (0, useSyncStore_1.syncStore)(headerVisible),
        });
        return list;
    }, [element, styles]);
    const onChange = React.useCallback((_list, map) => {
        const variantRaw = map.variant;
        const variant = typeof variantRaw === 'string' && (0, settings_1.isCalloutVariant)(variantRaw) ? variantRaw : settings_1.DEFAULT_VARIANT;
        setField('variant', variant === settings_1.DEFAULT_VARIANT ? undefined : variant);
        const hideHeaderRaw = map.hideHeader;
        setField('hideHeader', hideHeaderRaw ? true : undefined);
        const colorRaw = map.color;
        if (isDefaultable(colorRaw)) {
            if (colorRaw.def) {
                setField('color', undefined);
            }
            else {
                const v = String(colorRaw.value ?? '').trim();
                setField('color', v ? (0, colors_1.toHex)(v) : undefined);
            }
        }
        else if (typeof colorRaw === 'string' && colorRaw.trim()) {
            setField('color', (0, colors_1.toHex)(colorRaw.trim()));
        }
        else {
            setField('color', undefined);
        }
        const iconRaw = map.icon;
        if (isDefaultable(iconRaw)) {
            if (iconRaw.def) {
                setStringField('icon', undefined);
            }
            else {
                setStringField('icon', String(iconRaw.value ?? '').slice(0, 2));
            }
        }
        else if (typeof iconRaw === 'string') {
            setStringField('icon', iconRaw.slice(0, 2) || undefined);
        }
        else {
            setStringField('icon', undefined);
        }
        const titleRaw = map.title;
        if (isDefaultable(titleRaw)) {
            setStringField('title', titleRaw.def ? undefined : String(titleRaw.value ?? ''));
        }
        else if (typeof titleRaw === 'string') {
            setStringField('title', titleRaw || undefined);
        }
        else {
            setStringField('title', undefined);
        }
    }, [setField, setStringField]);
    return React.createElement(ArgsPane_1.ArgsPane, { item: item, params: params, onCancel: onCancel, onChange: onChange, minWidth: 303 });
};
exports.CalloutOptions = CalloutOptions;
